// 7-role identity system. The closed enum + per-role definitions lifted
// verbatim from DEMO/data.js (the canonical design source). Adding a role
// requires an ADR, a migration that ALTERs the role enum in Postgres, and
// updates to the canAssign matrix.
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Closed role enum
// ---------------------------------------------------------------------------
export const ROLES = [
    'super-admin',
    'manager',
    'employee',
    'parent',
    'child',
    'member',
    'guest',
];
export const RoleId = z.enum(ROLES);
export const ROLE_DEFS = {
    'super-admin': {
        id: 'super-admin',
        name: '超级管理员',
        nameEn: 'Super Admin',
        rank: 100,
        color: '#7a2c14',
        badge: '★',
        description: '系统所有者。配置、计费、跨工作室、销毁数据。一般 1–2 人持有。',
        capabilities: [
            'all',
            'site.config',
            'site.billing',
            'role.assign',
            'role.create',
            'workshop.create',
            'workshop.delete',
            'audit.read',
            'data.export',
            'data.purge',
        ],
        canAssign: ['super-admin', 'manager', 'employee', 'parent', 'child', 'member'],
    },
    manager: {
        id: 'manager',
        name: '管理者',
        nameEn: 'Manager',
        rank: 80,
        color: '#b04a1d',
        badge: '◆',
        description: '工作室主理人。负责一个或多个 group,审核内容、发文、签到、对家长负责。',
        capabilities: [
            'content.publish',
            'content.draft',
            'content.delete',
            'comment.moderate',
            'survey.create',
            'event.create',
            'workshop.run',
            'attendance.mark',
            'parent.message',
            'parent.invite',
            'employee.assign',
            'group.manage',
        ],
        canAssign: ['employee', 'parent', 'child', 'member'],
    },
    employee: {
        id: 'employee',
        name: '员工',
        nameEn: 'Employee',
        rank: 60,
        color: '#4a7c3c',
        badge: '●',
        description: '工作室员工 / TA / 助教。可记录考勤、协助回复家长,但无法删除内容。',
        capabilities: [
            'content.draft',
            'comment.reply',
            'comment.flag',
            'attendance.mark',
            'parent.message',
            'inner.read',
        ],
        canAssign: [],
    },
    parent: {
        id: 'parent',
        name: '家长',
        nameEn: 'Parent',
        rank: 40,
        color: '#2f6e8a',
        badge: '◐',
        description: '一个或多个孩子的监护人。可查看绑定孩子的考勤 / 进度 / 老师留言,并与员工或管理者私信。',
        capabilities: [
            'child.read',
            'child.attendance.read',
            'child.progress.read',
            'child.message.read',
            'parent.message.send',
            'survey.respond',
        ],
        canAssign: [],
    },
    child: {
        id: 'child',
        name: '学员',
        nameEn: 'Student',
        rank: 30,
        color: '#7a4ec7',
        badge: '○',
        description: '在工作室学习的孩子或学员。有自己的出勤记录;家长被绑定后可见。',
        capabilities: ['comment.post', 'survey.respond', 'forum.read.workshop'],
        canAssign: [],
    },
    member: {
        id: 'member',
        name: '普通会员',
        nameEn: 'Member',
        rank: 20,
        color: '#6b6658',
        badge: '·',
        description: '注册读者。可评论、订阅 newsletter、参与公开论坛。',
        capabilities: [
            'comment.post',
            'survey.respond',
            'newsletter.subscribe',
            'forum.read.public',
        ],
        canAssign: [],
    },
    guest: {
        id: 'guest',
        name: '访客',
        nameEn: 'Guest',
        rank: 10,
        color: '#a39f95',
        badge: '–',
        description: '未登录访客。仅可阅读公开文章。',
        capabilities: ['article.read.public'],
        canAssign: [],
    },
};
/** Convenience: ranks ordered descending. */
export const ROLES_BY_RANK_DESC = ROLES.slice().sort((a, b) => ROLE_DEFS[b].rank - ROLE_DEFS[a].rank);
/** O(1) rank lookup. */
export function rankOf(role) {
    return ROLE_DEFS[role].rank;
}
/** Whether `assigner` may grant `target` to another user. */
export function canAssign(assigner, target) {
    return ROLE_DEFS[assigner].canAssign.includes(target);
}
/** Whether `viewerRoles` includes a role with rank ≥ `minRank`. */
export function hasMinRank(viewerRoles, minRank) {
    for (const r of viewerRoles) {
        if (ROLE_DEFS[r].rank >= minRank)
            return true;
    }
    return false;
}
//# sourceMappingURL=role.js.map